import type { ForestImpact } from "@/lib/settings"

export interface SDGEntry {
  number: number
  title: string
  iconUrl: string
  subtitle?: string
  description?: string
  url: string
  color?: string
  metricFromForest?: "co2CapturedTonnes" | "numberOfTrees"
}

export interface EnrichedSDG extends SDGEntry {
  metric?: string
}

const SDG_ICON = (n: number) =>
  `/images/sdg/SDG_Icons_Inverted_Transparent_WEB-${String(n).padStart(2, "0")}.png`

export const SDG_CATALOGUE: SDGEntry[] = [
  {
    number: 13,
    title: "Climate Action",
    subtitle: "Take urgent action to combat climate change and its impacts.",
    description: "Trees are planted for every project, funding reforestation through Tree-Nation.",
    metricFromForest: "co2CapturedTonnes",
    iconUrl: SDG_ICON(13),
    color: "from-green-600 to-green-800",
    url: "https://sdgs.un.org/goals/goal13",
  },
  {
    number: 15,
    title: "Life on Land",
    subtitle: "Protect, restore and promote sustainable use of terrestrial ecosystems.",
    description:
      "Tree planting supports global reforestation, biodiversity, and long-term soil and water restoration, in cooperation with certified partners.",
    metricFromForest: "numberOfTrees",
    iconUrl: SDG_ICON(15),
    color: "from-green-500 to-green-700",
    url: "https://sdgs.un.org/goals/goal15",
  },
  {
    number: 9,
    title: "Industry, Innovation and Infrastructure",
    subtitle:
      "Build resilient infrastructure, promote inclusive and sustainable industrialization and foster innovation.",
    description:
      "GIOF builds modular, efficient digital platforms using modern, low-impact tooling. That reduces server waste and increases accessibility.",
    iconUrl: SDG_ICON(9),
    color: "from-orange-500 to-orange-700",
    url: "https://sdgs.un.org/goals/goal9",
  },
  {
    number: 12,
    title: "Responsible Consumption and Production",
    subtitle: "Ensure sustainable consumption and production patterns.",
    description:
      "Projects are designed with digital sustainability in mind, reducing load size, optimizing performance, and avoiding overconsumption of hosting resources.",
    iconUrl: SDG_ICON(12),
    color: "from-yellow-500 to-yellow-700",
    url: "https://sdgs.un.org/goals/goal12",
  },
  {
    number: 17,
    title: "Partnerships for the Goals",
    subtitle: "Revitalize the global partnership for sustainable development.",
    description:
      "GIOF works with clients, agencies, and technology providers to embed sustainability into real-world digital projects.",
    iconUrl: SDG_ICON(17),
    color: "from-blue-600 to-blue-800",
    url: "https://sdgs.un.org/goals/goal17",
  },
]

export function enrichSDGsWithForestMetrics(
  forest: ForestImpact | null,
  catalogue: SDGEntry[] = SDG_CATALOGUE
): EnrichedSDG[] {
  return catalogue.map((sdg) => {
    if (!forest || !sdg.metricFromForest) {
      return sdg
    }

    if (sdg.metricFromForest === "co2CapturedTonnes") {
      return {
        ...sdg,
        metric: `${forest.co2CapturedTonnes.toLocaleString()} tonnes of CO₂ estimated to be captured over the trees' first 10 years.`,
      }
    }

    return {
      ...sdg,
      metric: `${forest.numberOfTrees.toLocaleString()} trees planted and growing.`,
    }
  })
}

/** Compact SDG list for home-page initiative strip */
export function sdgIconsForInitiative(catalogue: SDGEntry[] = SDG_CATALOGUE) {
  return catalogue.map(({ number, title, iconUrl }) => ({ number, title, iconUrl }))
}
